import { Folder } from "lucide-react";
import { DatePicker } from "@/components/ui/DatePicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import { Button as NewButton } from "@/components/ui/NewButton";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useParams } from '@tanstack/react-router';
import { toast } from 'react-hot-toast';
import { useFolderTree } from "@/hooks/useFolders";
import { backendClient } from "@/lib/api-client";
import { getSourceDisplayName, isExternalSyncRestrictedSource, normalizeSource } from "@/lib/sourceUtils";
import { flattenFolderTree } from "@/utils/document-utils";
import Loading from "./Loading";

const formSchema = z.object({
  date: z.union([z.date(), z.string()]).optional().nullable(),
  summary: z.string().optional(),
  is_draft: z.string()
});

interface EditCaseFileDialogProps {
  row: any;
  onSuccess?: () => void;
  isOpen: boolean;
  onClose: () => void;
}

export function EditCaseFileDialog({ row, onSuccess, isOpen, onClose }: EditCaseFileDialogProps) {
  const { slug } = useParams({ from: '/matter/$slug' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const source = normalizeSource(row.source);
  const isFolderLockedForSource = isExternalSyncRestrictedSource(source);
  const externalSyncSourceLabel = getSourceDisplayName(source, "an external source");
  const finalStatusDisabled = row.is_draft && row.is_drafting_managed;

  // Use the folder tree hook (same as MoveDocumentsDialog)
  const { folderTree, isLoading: isLoadingFolders } = useFolderTree(slug);

  // Flatten folder tree for selection with visual hierarchy
  const flattenedFolders = folderTree ? flattenFolderTree(folderTree) : [];

  // If document type is included, extend the schema
  const fullFormSchema = formSchema.extend({
        document_type: z.string(),
      });

  // Helper to get a valid date or null
  const getValidDate = (date: any): Date | null => {
    if (!date) return null;
    if (date instanceof Date) {
      return isNaN(date.getTime()) ? null : date;
    }
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const form = useForm({
    resolver: zodResolver(fullFormSchema),
    defaultValues: {
      date: getValidDate(row.date),
      summary: row.summary,
      is_draft: row.is_draft ? "true" : "false",
      document_type: row.document_type?.id?.toString() || "",
    },
  });

  const onSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);

      // Format date exactly as selected, no timezone conversion
      // Handle null/undefined dates
      let formattedDate: string | null = null;
      if (data.date) {
        if (data.date instanceof Date) {
          // Use local date components to avoid timezone shifts
          const year = data.date.getFullYear();
          const month = String(data.date.getMonth() + 1).padStart(2, '0');
          const day = String(data.date.getDate()).padStart(2, '0');
          formattedDate = `${year}-${month}-${day}`;
        } else if (typeof data.date === 'string') {
          formattedDate = data.date.split('T')[0];
        }
      }

      // Get the draft status
      const isDraft = data.is_draft === "true";

      const metadata = {
        date: formattedDate, // Can be null if no date selected
        summary: data.summary || "",
        sender: row.from || [],
        recipient: row.to || [],
        deadlines: [],
      };

      await backendClient.put(
        `/correspondence/${slug}/edit-metadata`,
        { metadata },
        { params: { document_guid: row.guid } }
      );

      if (isDraft !== row.is_draft) {
        await backendClient.post(`/correspondence/${slug}/toggle-draft-status`, {
          document_guids: [row.guid],
          is_draft: isDraft,
        });
      }

      // If folder is changed, move document to new folder
      const currentFolderId = row.document_type?.id?.toString() || "";
      if (!isFolderLockedForSource && data.document_type && data.document_type !== currentFolderId) {
        const newFolderId = parseInt(data.document_type);
        if (newFolderId) {
          await backendClient.post(`/matter/${slug}/move-documents-to-folder`, {
            file_reference_guids: [row.id],
            folder_id: newFolderId
          });
        }
      }

      // Success - close dialog and show success message
      toast.success("Document updated");

      // Call the onSuccess callback to refresh data if provided
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }

      // Close the dialog
      onClose();
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error("We were unable to update the document. Please reach out to support@crimson.law.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(newOpen) => {
        if (!newOpen) {
          form.reset({
            date: getValidDate(row.date),
            summary: row.summary,
            is_draft: row.is_draft ? "true" : "false",
            document_type: row.document_type?.slug || "",
          });
          onClose();
        }
      }}
    >
      <DialogContent
        className="min-w-fit"
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>Edit Document Details</DialogTitle>
          <DialogDescription>Update the document details below.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-4">
            <div className="flex flex-col gap-4">
              {/* Date Field */}
              <FormField
                name="date"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Date</FormLabel>
                    <DatePicker
                      initialValue={field.value ? getValidDate(field.value) ?? undefined : undefined}
                      onChange={field.onChange}
                      disabledDates={(date) =>
                        date > new Date() || date < new Date("1900-01-01")
                      }
                      triggerClassName="w-[200px]"
                      showTopControls
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Summary Field */}
              <FormField
                name="summary"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Summary</FormLabel>
                    <FormControl>
                      <Textarea {...field} className="w-full min-h-[150px] custom-scrollbar" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Status Field */}
              <FormField
                name="is_draft"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Status</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="true">Draft</SelectItem>
                        <SelectItem value="false" disabled={finalStatusDisabled}>Final</SelectItem>
                      </SelectContent>
                    </Select>
                    {finalStatusDisabled && (
                      <p className="text-xs text-slate-500">
                        Drafts created in Drafting cannot currently be marked final.
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Folder Selection Field */}
              <FormField
                name="document_type"
                control={form.control}
                render={({ field }) => (
                  <FormItem className="flex flex-col gap-2">
                    <FormLabel>Folder</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={isLoadingFolders || isFolderLockedForSource}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select folder">
                            {isLoadingFolders ? (
                              <div className="flex items-center">
                                <Loading text='Loading folders...' />
                              </div>
                            ) : field.value ? (
                              flattenedFolders.find(folder => folder.original.folder_id.toString() === field.value)?.name || "Select folder"
                            ) : (
                              "Select folder"
                            )}
                          </SelectValue>
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectGroup>
                          <SelectLabel>Folders</SelectLabel>
                          {flattenedFolders.map((folder) => (
                            <SelectItem
                              key={`edit-folder-${folder.original.folder_id}`}
                              value={folder.original.folder_id.toString()}
                              className="group cursor-pointer hover:bg-accent/50 transition-colors duration-150"
                            >
                              <div
                                className="flex items-center w-full"
                                style={{ paddingLeft: `${folder.depth * 16}px` }}
                              >
                                <Folder className="h-4 w-4 mr-2 shrink-0 text-amber-500/40 fill-amber-400/15" />
                                <span className="truncate font-medium">{folder.name}</span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      </SelectContent>
                    </Select>
                    {isFolderLockedForSource && (
                      <p className="text-sm text-muted-foreground">
                        This document is synced from {externalSyncSourceLabel} and cannot be moved
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter className="mt-4">
              <div className="flex flex-row gap-2">
                <NewButton
                  variant='outline'
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    form.reset();
                    onClose();
                  }}
                  disabled={isSubmitting}
                  text='Cancel'
                />
                <NewButton
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  loadingText='Saving'
                  text='Save changes'
                />
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
